// Admin → Feedback: tester comments from the field-level feedback system.
// Mobile-friendly stacked cards; Open (default) / Done / All filters;
// Mark done / Reopen; expandable snapshot JSON per item.
import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'

type FeedbackItem = {
  id: number
  element_key: string
  element_label: string
  current_value: string | null
  comment: string
  author: string
  page: string
  snapshot_json: string | null
  status: 'open' | 'done'
  created_at: string
  updated_at: string
}

function prettySnapshot(raw: string | null): string {
  if (!raw) return '(no snapshot)'
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

export default function AdminFeedback() {
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'open' | 'done' | 'all'>('open')
  const [busy, setBusy] = useState<number | null>(null)

  const reload = useCallback(() => {
    setLoading(true)
    api
      .get<{ items: FeedbackItem[] }>(`/api/feedback?status=${filter}`)
      .then((d) => setItems(d.items))
      .finally(() => setLoading(false))
  }, [filter])

  useEffect(() => {
    reload()
  }, [reload])

  async function setStatus(id: number, status: 'open' | 'done') {
    setBusy(id)
    try {
      await api.patch(`/api/feedback/${id}`, { status })
      if (filter === 'all') {
        setItems((list) => list.map((i) => (i.id === id ? { ...i, status } : i)))
      } else {
        setItems((list) => list.filter((i) => i.id !== id))
      }
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(['open', 'done', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
              filter === s ? 'bg-brand-600 text-white' : 'bg-ink-900 border border-ink-800 text-ink-100'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-ink-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="card text-center text-ink-500">No {filter === 'all' ? '' : filter} feedback.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-white break-words">{item.element_label}</div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    {item.page} · {item.author} · {item.created_at.slice(0, 16).replace('T', ' ')}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`chip border text-xs ${
                      item.status === 'open'
                        ? 'text-amber-400 border-amber-500/30'
                        : 'text-emerald-400 border-emerald-500/30'
                    }`}
                  >
                    {item.status}
                  </span>
                  <button
                    onClick={() => setStatus(item.id, item.status === 'open' ? 'done' : 'open')}
                    disabled={busy === item.id}
                    className={`text-xs px-2 py-1 rounded text-white disabled:opacity-50 ${
                      item.status === 'open' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-ink-800 hover:bg-ink-700'
                    }`}
                  >
                    {busy === item.id ? '…' : item.status === 'open' ? 'Mark done' : 'Reopen'}
                  </button>
                </div>
              </div>

              <p className="mt-2 text-sm text-ink-100 whitespace-pre-wrap break-words">{item.comment}</p>

              {item.current_value && (
                <div className="mt-1 text-xs text-ink-500 break-words">
                  Value at click: <span className="text-ink-100">{item.current_value}</span>
                </div>
              )}

              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-ink-500 hover:text-ink-100">
                  Snapshot ({item.element_key.slice(0, 60)})
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-ink-950 p-3 text-xs text-ink-100">
                  {prettySnapshot(item.snapshot_json)}
                </pre>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
